import { useEffectWithController } from '@carvana/vdp-react-hooks';
import equals from 'ramda/es/equals';
import { useContext, useEffect, useRef } from 'react';
import ErrorContext from '~/contexts/ErrorContext';
import InventoryContext from '~/contexts/InventoryContext';
import UserContext from '~/contexts/UserContext';
import type { Vehicle } from '~/contracts';
import useMemoCompare from '~/hooks/useMemoCompare';
import fetchAvailability from '~/services/fetchAvailability';
import generateErrorLogger, { logError } from '~/utilities/errorHandlers';
import { addAvailabilityToVehicles } from './helpers';

const useGetAvailability = () => {
  const {
    inventory: {
      data: {
        searchRequestId,
        inventory: { vehicles }
      },
      setData: setInventory
    }
  } = useContext(InventoryContext);

  const { updateResponseErrors } = useContext(ErrorContext);
  const { user, userLocation } = useContext(UserContext);

  // We use this to ensure we're only re-fetching if the vehicleIds or downPaymentDetails have changed on the vehicles object.
  const stableVehicles = useMemoCompare<Array<Vehicle>>(
    vehicles,
    (prev, next) =>
      equals(
        prev.map(vehicle => [
          vehicle.vehicleId,
          vehicle.price?.downPaymentDetails
        ]),
        next.map(vehicle => [
          vehicle.vehicleId,
          vehicle.price?.downPaymentDetails
        ])
      )
  );

  const zipRef = useRef(userLocation.zip);
  useEffect(() => {
    zipRef.current = userLocation.zip;
  }, [userLocation.zip]);

  const shouldCallAvailability = stableVehicles.length && user.isLoaded;

  useEffectWithController(
    controller => {
      if (!shouldCallAvailability) return;

      fetchAvailability({
        zip: zipRef.current,
        userId: user.data.userId,
        vehicles: stableVehicles,
        searchRequestId,
        controller
      })
        .then(res => {
          if (res.data.status === 'Succeeded') {
            const updatedVehicles = addAvailabilityToVehicles(
              stableVehicles,
              res.data.content ?? []
            );

            setInventory(
              inventoryData => ({
                ...inventoryData,
                inventory: {
                  ...inventoryData.inventory,
                  vehicles: updatedVehicles
                }
              }),
              { shouldSetLoaded: false }
            );

            if (updatedVehicles.some(v => v.pickupInfo || v.deliveryInfo)) {
              updateResponseErrors({ availabilityError: null });
            } else {
              const vehicle = res.data.content?.find(
                v =>
                  v.soonestDelivery?.errorMessage ||
                  v.soonestPickup?.errorMessage
              );

              const errorMessage =
                vehicle?.soonestDelivery?.errorMessage ||
                vehicle?.soonestPickup?.errorMessage;

              updateResponseErrors({ availabilityError: errorMessage });
            }
          } else {
            logError(new Error(res.data.errorMessage), {
              location: 'Availability'
            });
            updateResponseErrors({ availabilityError: res.data.errorMessage });
          }
        })
        .catch(
          generateErrorLogger(
            'Availability',
            {},
            {
              callback: () => {
                updateResponseErrors({
                  availabilityError: 'Failed to fetch availability.'
                });
              },
              controller
            }
          )
        );
    },
    [
      searchRequestId,
      shouldCallAvailability,
      setInventory,
      stableVehicles,
      updateResponseErrors,
      user.data.userId
    ]
  );
};

export default useGetAvailability;
